import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { useModal } from "../../lib/ModalContext";
import { api } from "../../lib/api";
import { EASE } from "../../lib/motion";

const WEALTHELITE_BASE = "https://wealthelite.in";
const WE_SCRIPTS = [
    "https://code.jquery.com/jquery-3.7.1.min.js",
    `${WEALTHELITE_BASE}/js/aes.js`,
    `${WEALTHELITE_BASE}/js/login_script.js`,
    `${WEALTHELITE_BASE}/js/formValidation.js`,
    `${WEALTHELITE_BASE}/js/ajaxCore.js`,
    `${WEALTHELITE_BASE}/js/login.js`,
];

// Lazy load required scripts in order, exactly once.
let scriptsLoadedPromise = null;
const loadPortalScripts = () => {
    if (scriptsLoadedPromise) return scriptsLoadedPromise;
    scriptsLoadedPromise = (async () => {
        for (const src of WE_SCRIPTS) {
            // Skip if already present
            if (document.querySelector(`script[src="${src}"]`)) continue;
            await new Promise((resolve) => {
                const s = document.createElement("script");
                s.src = src;
                s.async = false;
                s.onload = resolve;
                s.onerror = () => resolve(); // resolve even on failure so we don't hang
                document.body.appendChild(s);
            });
        }
    })();
    return scriptsLoadedPromise;
};

// Unify auth-failure messages while keeping operational signals distinct.
const ET_MESSAGES = {
    "1": "Username or password is incorrect",
    "2": "Username or password is incorrect",
    "3": "Your access has been blocked. Please contact admin.",
    "4": "Login is currently disabled. Please contact admin.",
};

const Shell = ({ children, title, eyebrow }) => {
    const { close } = useModal();
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="fixed inset-0 z-[80] flex items-center justify-center p-4 md:p-8"
            onClick={close}
            style={{ background: "rgba(10,10,10,0.45)", backdropFilter: "blur(8px)" }}
            data-testid="modal-backdrop-portal-login"
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 16 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97, y: 10 }}
                transition={{ duration: 0.7, ease: EASE }}
                onClick={(e) => e.stopPropagation()}
                className="glass-modal w-full max-w-[420px] max-h-[92vh] overflow-hidden relative flex flex-col"
                data-testid="modal-portal-login"
            >
                <button
                    onClick={close}
                    aria-label="Close"
                    className="absolute top-5 right-5 z-20 w-9 h-9 rounded-full bg-white/80 hover:bg-white flex items-center justify-center transition-colors shadow font-body text-base"
                    data-testid="modal-close-portal-login"
                >
                    ×
                </button>
                <div data-lenis-prevent className="overflow-y-auto thin-scroll flex-1 p-8 md:p-10">
                    {eyebrow && <div className="text-xs uppercase tracking-[0.22em] text-oth-ink3 font-body text-center">{eyebrow}</div>}
                    <div className="mt-2 font-display text-3xl md:text-4xl font-semibold tracking-tight text-center">{title}</div>
                    <div className="mt-6">{children}</div>
                </div>
            </motion.div>
        </motion.div>
    );
};

const LoginForm = ({ role, error, setError, onForgot, callbackUrl }) => {
    const formRef = useRef(null);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = (e) => {
        // Allow the legacy GetSubmitLogin() handler (loaded via WE scripts) to fire,
        // but make sure we always submit even if it isn't loaded yet.
        setSubmitting(true);
        setError("");
        const fn = typeof window !== "undefined" ? window.GetSubmitLogin : undefined;
        if (typeof fn === "function") {
            const ok = fn();
            if (ok === false) {
                e.preventDefault();
                setSubmitting(false);
                return;
            }
        }
        // form submits natively, browser navigates to WealthElite
    };

    return (
        <form
            ref={formRef}
            name="signinForm"
            id="signinForm"
            action={`${WEALTHELITE_BASE}/Login/login/validate-user`}
            method="POST"
            onSubmit={handleSubmit}
            className="space-y-4"
            data-testid="portal-login-form"
        >
            <div className="space-y-2">
                <label htmlFor="inputEmail" className="text-xs uppercase tracking-[0.22em] text-oth-ink3 font-body">Username</label>
                <input
                    id="inputEmail"
                    name="username"
                    type="text"
                    autoComplete="username"
                    placeholder="Enter your user name"
                    required
                    className="field"
                    data-testid="portal-username-input"
                />
            </div>

            <div className="space-y-2">
                <label htmlFor="inputPassword" className="text-xs uppercase tracking-[0.22em] text-oth-ink3 font-body">Password</label>
                <input
                    id="inputPassword"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    placeholder="••••••"
                    required
                    className="field"
                    data-testid="portal-password-input"
                />
            </div>

            <input type="hidden" name="loginFor" id="loginFor" value={role} readOnly />
            <input type="hidden" name="type" id="type" value={role} readOnly />
            <input type="hidden" name="loginType" id="loginType" value={role} readOnly />
            <input type="hidden" name="callbackUrl" id="callbackUrl" defaultValue={callbackUrl} />

            {/* Alert containers used by legacy scripts */}
            <p id="loginPageAlertError" className="text-sm text-[#dc3545] font-body min-h-[1.25rem]" style={{ display: error ? "block" : "none" }} data-testid="portal-error">{error}</p>
            <p id="loginPageAlertSuccess" className="text-sm text-[#28a745] font-body" style={{ display: "none" }} />

            <button
                type="submit"
                disabled={submitting}
                className="btn-pill btn-pill-dark w-full"
                data-testid="portal-submit"
            >
                {submitting ? "Verifying…" : "Sign in"}
                <ArrowUpRight size={16} />
            </button>

            <div className="pt-2 text-center">
                <button
                    type="button"
                    onClick={onForgot}
                    className="text-sm text-oth-ink2 hover:text-oth-blue underline-offset-4 hover:underline font-body transition-colors"
                    data-testid="portal-forgot-link"
                >
                    Forgot password?
                </button>
            </div>
        </form>
    );
};

const ForgotUsernameStep = ({ role, onBack, onOtpSent, setError }) => {
    const [userName, setUserName] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!userName.trim()) return;
        setBusy(true);
        setError("");
        try {
            const r = await api.post("/portal/send-forget-password-otp", {
                userName: userName.trim(),
                type: role,
                source: "link",
            });
            const data = r.data || {};
            if (data.msgType === "success" || data.OtpMobileNo || data.mobileNumber) {
                toast.success(data.msg || "OTP sent to your registered mobile number.");
                onOtpSent({
                    OtpMobileNo: data.OtpMobileNo || data.mobileNumber || "",
                    userName: userName.trim(),
                });
            } else {
                const msg = data.msg || "Could not start password reset. Please try again.";
                setError(msg);
            }
        } catch (err) {
            setError(err?.response?.data?.detail || "Could not reach the portal. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={submit} className="space-y-4" data-testid="portal-forgot-username-form">
            <div className="space-y-2">
                <label htmlFor="forgotUserName" className="text-xs uppercase tracking-[0.22em] text-oth-ink3 font-body">Username</label>
                <input
                    id="forgotUserName"
                    type="text"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    placeholder="Enter your user name"
                    required
                    className="field"
                    data-testid="portal-forgot-username-input"
                />
                <p className="text-xs text-oth-ink3 font-body">We'll send a one-time password to the mobile number registered on your account.</p>
            </div>

            <button type="submit" disabled={busy} className="btn-pill btn-pill-dark w-full" data-testid="portal-forgot-send-otp">
                {busy ? "Sending OTP…" : "Send OTP"} <ArrowUpRight size={16} />
            </button>

            <button
                type="button"
                onClick={onBack}
                className="block mx-auto text-sm text-oth-ink2 hover:text-oth-blue underline-offset-4 hover:underline font-body transition-colors"
                data-testid="portal-forgot-back"
            >
                ← Back to sign in
            </button>
        </form>
    );
};

const ForgotOtpStep = ({ otpMobileSeed, onBack, setError }) => {
    const [OtpMobileNo, setOtpMobileNo] = useState(otpMobileSeed?.OtpMobileNo || "");
    const [mobileOtp, setMobileOtp] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!OtpMobileNo.trim() || !mobileOtp.trim()) return;
        setBusy(true);
        setError("");
        try {
            const r = await api.post("/portal/submit-forget-password-otp", {
                OtpMobileNo: OtpMobileNo.trim(),
                mobileOtp: mobileOtp.trim(),
                resendOtp: "0",
            });
            const data = r.data || {};
            if (data.msgType === "success") {
                toast.success(data.msg || "OTP verified. Check your email for the password reset link.");
                onBack(); // return to login screen
            } else {
                setError(data.msg || "Invalid OTP. Please try again.");
            }
        } catch (err) {
            setError(err?.response?.data?.detail || "Could not verify OTP. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={submit} className="space-y-4" data-testid="portal-forgot-otp-form">
            <div className="space-y-2">
                <label htmlFor="OtpMobileNo" className="text-xs uppercase tracking-[0.22em] text-oth-ink3 font-body">Mobile number</label>
                <input
                    id="OtpMobileNo"
                    type="text"
                    inputMode="tel"
                    autoComplete="tel"
                    value={OtpMobileNo}
                    onChange={(e) => setOtpMobileNo(e.target.value)}
                    placeholder="Registered mobile number"
                    required
                    className="field"
                    data-testid="portal-otp-mobile-input"
                />
            </div>

            <div className="space-y-2">
                <label htmlFor="mobileOtp" className="text-xs uppercase tracking-[0.22em] text-oth-ink3 font-body">OTP</label>
                <input
                    id="mobileOtp"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={mobileOtp}
                    onChange={(e) => setMobileOtp(e.target.value.replace(/[^0-9]/g, ""))}
                    placeholder="6-digit OTP"
                    required
                    className="field tracking-[0.4em] text-center"
                    data-testid="portal-otp-input"
                />
            </div>

            <button type="submit" disabled={busy} className="btn-pill btn-pill-dark w-full" data-testid="portal-otp-submit">
                {busy ? "Verifying…" : "Verify OTP"} <ArrowUpRight size={16} />
            </button>

            <button
                type="button"
                onClick={onBack}
                className="block mx-auto text-sm text-oth-ink2 hover:text-oth-blue underline-offset-4 hover:underline font-body transition-colors"
                data-testid="portal-otp-back"
            >
                ← Back to sign in
            </button>
        </form>
    );
};

export const PortalLoginModal = ({ content, initialError }) => {
    const role = "CLIENT"; // single role — pills removed
    const [mode, setMode] = useState("login"); // login | forgot_username | forgot_otp
    const [otpSeed, setOtpSeed] = useState(null);
    const [error, setError] = useState(initialError || "");

    const callbackUrl = content?.brand?.portal_callback_url || (typeof window !== "undefined" ? window.location.origin : "");

    // Lazy-load WealthElite scripts (jQuery + AES + login handlers) on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { loadPortalScripts(); }, []);

    const titleByMode = {
        login: "Welcome back",
        forgot_username: "Reset your password",
        forgot_otp: "Verify your OTP",
    };
    const eyebrowByMode = {
        login: null,
        forgot_username: "Step 1 of 2",
        forgot_otp: "Step 2 of 2",
    };

    return (
        <Shell title={titleByMode[mode]} eyebrow={eyebrowByMode[mode]}>
            {mode === "login" && (
                <LoginForm
                    role={role}
                    error={error}
                    setError={setError}
                    onForgot={() => { setError(""); setMode("forgot_username"); }}
                    callbackUrl={callbackUrl}
                />
            )}
            {mode === "forgot_username" && (
                <ForgotUsernameStep
                    role={role}
                    onBack={() => { setError(""); setMode("login"); }}
                    onOtpSent={(seed) => { setOtpSeed(seed); setMode("forgot_otp"); }}
                    setError={setError}
                />
            )}
            {mode === "forgot_otp" && (
                <ForgotOtpStep
                    otpMobileSeed={otpSeed}
                    onBack={() => { setError(""); setMode("login"); }}
                    setError={setError}
                />
            )}

            {/* Shared error display for forgot-password steps */}
            {(mode !== "login" && error) && (
                <p className="mt-4 text-sm text-[#dc3545] font-body text-center" data-testid="portal-forgot-error">{error}</p>
            )}
        </Shell>
    );
};

// Helper: parses ?et=N from URL on page load and returns the corresponding error.
export const consumePortalEtError = () => {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    const et = params.get("et");
    if (!et || !ET_MESSAGES[et]) return null;
    // Clean the URL so refreshing doesn't re-show the error.
    params.delete("et");
    const newSearch = params.toString();
    const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : "") + window.location.hash;
    window.history.replaceState({}, "", newUrl);
    return ET_MESSAGES[et];
};
